import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { SearchTextField } from "@/controls/quick-filter-textfield/SearchTextField";
import { useGridStyles } from "@/pages/laboratory/styles/gridStyles";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid2 as Grid, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { useLadlesOptionsFunctions } from "../hooks/useLadlesOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { UpsertDeleteLadleModal } from "./modals/UpsertDelete";

type Props = {
	getCatalogLadlesFromAPI: Function;
	isLoading: boolean;
};

export const TableLadle = ({ getCatalogLadlesFromAPI, isLoading }: Props) => {
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [ladleId, setLadleId] = useState<number | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [quickFilterText, setQuickFilterText] = useState("");

	const { getMenuOptions } = useLadlesOptionsFunctions({
		setLadleId,
		setShowModal,
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { rows, columnDefs } = useTableData({
		setLadleId,
		setShowModal,
		showContextMenu,
	});

	const { columnDefaults } = useGridDefinitions({
		OnEdit: (data: any) => {
			setShowModal("update");
			setLadleId(data.Id);
		},
	});

	columnDefaults.floatingFilter = false;

	const onRowDoubleClicked = (event: any) => {
		setLadleId(event.data.LadleId);
		setShowModal("update");
	};

	const getContextMenuItems = React.useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "ladleContext");
			return [];
		},
		[rows],
	);

	const onModalHide = (shouldUpdate: boolean) => {
		if (shouldUpdate)
			getCatalogLadlesFromAPI().then(() => {
				gridApi?.paginationGoToLastPage();
			});
		setShowModal("");
	};

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else {
			gridApi?.hideOverlay();
		}
	}, [isLoading, rows, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "ladleContext",
			getOptions: getMenuOptions,
		});
	}, []);

	const classes = useGridStyles();

	return (
		<>
			<Grid size={{ xs: 12 }} container justifyContent="center" spacing={1}>
				<Grid size={{ xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Ladles
					</Typography>
				</Grid>

				<Grid
					size={{ xs: 12 }}
					container
					justifyContent="flex-end"
					alignContent="center"
				>
					<Grid size={{ xs: 12 }} style={{ marginBottom: 10 }}>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search ladles"
						/>
					</Grid>
					<Grid
						size={{ xs: 12 }}
						style={{ height: "68vh" }}
						className={classes.borderAgGridTable}
					>
						<div style={{ height: "100%", width: "100%" }}>
							<AgGridReact
								rowData={rows || []}
								columnDefs={columnDefs as any}
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								getRowId={(params) => String(params.data.LadleId)}
								onGridReady={(params) => setGridApi(params.api)}
								enableCellTextSelection={true}
								suppressContextMenu={false}
								allowContextMenuWithControlKey={false}
								rowSelection="single"
								quickFilterText={quickFilterText}
								loading={isLoading}
							/>
						</div>
					</Grid>

					<Grid size={{ xs: 12, md: "auto" }} style={{ marginTop: 10 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							style={{ minWidth: 250 }}
							onClick={() => setShowModal("create")}
						>
							NEW LADLE
						</Button>
					</Grid>
				</Grid>
			</Grid>

			<UpsertDeleteLadleModal
				LadleId={ladleId}
				onHide={onModalHide}
				modal={showModal}
			/>
		</>
	);
};
